/**
 * Theme preset metadata.
 *
 * Token VALUES live in src/index.css as `[data-theme="<id>"]` blocks, not here.
 * That split is deliberate: the boot script in public/index.html applies a theme
 * before the app bundle parses, so it can only set an attribute — it cannot read
 * a JS module. Keeping values in CSS means there is exactly one source of truth
 * and no flash of the wrong brand on first paint.
 *
 * This module carries only what the admin picker needs to render.
 *
 * Preset ids must stay brand-neutral (generic adjectives). They are visible in
 * the /api/settings/ payload and as <html data-theme>, so an id naming a
 * customer or the parent platform would undermine whitelabel non-disclosure.
 *
 * ⚠️ Adding a preset means editing THREE places:
 *   1. this list
 *   2. a [data-theme="<id>"] block in src/index.css
 *   3. the VALID array in the boot script in public/index.html
 */

export const DEFAULT_THEME_PRESET = 'default';

export const THEME_PRESETS = [
  {
    id: 'default',
    label: 'Default',
    description: 'The standard indigo and violet palette.',
    swatches: ['#667eea', '#764ba2', '#7c3aed'],
  },
  {
    id: 'slate',
    label: 'Slate',
    description: 'Cool blue and teal with a dark slate sidebar.',
    swatches: ['#0284c7', '#0f766e', '#0f172a'],
  },
  {
    id: 'midnight',
    label: 'Midnight',
    description: 'Deep indigo and violet with a near-black sidebar.',
    swatches: ['#7c3aed', '#4338ca', '#1e1b4b'],
  },
  {
    id: 'forest',
    label: 'Forest',
    description: 'Emerald and teal with a deep green sidebar.',
    swatches: ['#047857', '#0f766e', '#064e3b'],
  },
  {
    id: 'ember',
    label: 'Ember',
    description: 'Burnt orange and amber with a dark ember sidebar.',
    swatches: ['#c2410c', '#b45309', '#431407'],
  },
];

export const THEME_PRESET_IDS = THEME_PRESETS.map((p) => p.id);

/**
 * Sidebar colour scheme. An axis independent of THEME_PRESETS, so any preset
 * pairs with either nav style rather than doubling the preset list.
 *
 * ⚠️ Adding one means editing THREE places, same as a theme preset:
 *   1. this list
 *   2. a [data-nav="<id>"] block in src/index.css
 *   3. the VALID_NAV array in the boot script in public/index.html
 */
export const DEFAULT_NAV_STYLE = 'dark';

export const NAV_STYLES = [
  { id: 'dark', label: 'Dark sidebar', description: 'Coloured sidebar with light text.', swatch: '#4f46e5' },
  { id: 'light', label: 'Light sidebar', description: 'White sidebar with dark text.', swatch: '#ffffff' },
];

export const NAV_STYLE_IDS = NAV_STYLES.map((n) => n.id);

/**
 * Main content layout for the user-facing pages. A third independent axis.
 *
 * Unlike themes and nav style, this one is NOT purely CSS - it changes what
 * React renders (title block, whether the form is inline, section card
 * chrome), so components read the value from useSettings rather than relying
 * on an attribute. It is listed here to keep all three axes in one place.
 */
export const DEFAULT_CONTENT_LAYOUT = 'classic';

export const CONTENT_LAYOUTS = [
  {
    id: 'classic',
    label: 'Classic',
    description: 'Highlighted action card, subscription panel, form opened on demand.',
  },
  {
    id: 'focused',
    label: 'Focused',
    description: 'Plain page title, form always visible, compact section cards.',
  },
];

export const CONTENT_LAYOUT_IDS = CONTENT_LAYOUTS.map((l) => l.id);

export function isValidContentLayout(id) {
  return typeof id === 'string' && CONTENT_LAYOUT_IDS.includes(id);
}

export function resolveContentLayout(id) {
  return isValidContentLayout(id) ? id : DEFAULT_CONTENT_LAYOUT;
}

export const THEME_STORAGE_KEY = 'app_theme_preset';
export const NAV_STORAGE_KEY = 'app_nav_style';

export function isValidThemePreset(id) {
  return typeof id === 'string' && THEME_PRESET_IDS.includes(id);
}

export function isValidNavStyle(id) {
  return typeof id === 'string' && NAV_STYLE_IDS.includes(id);
}

/**
 * Apply a preset to the document and remember it for the next cold start.
 *
 * An unknown or missing id falls back to the default rather than rendering
 * an unthemed page — a whitelabel is a separate deployment, so a bad value
 * can't be reproduced locally and must never produce a broken-looking UI.
 */
export function applyThemePreset(id) {
  const preset = isValidThemePreset(id) ? id : DEFAULT_THEME_PRESET;

  if (typeof document !== 'undefined') {
    if (preset === DEFAULT_THEME_PRESET) {
      // :root already holds the default tokens; no attribute needed.
      document.documentElement.removeAttribute('data-theme');
    } else {
      document.documentElement.setAttribute('data-theme', preset);
    }
  }

  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, preset);
  } catch (e) {
    // Private mode / storage disabled. The theme still applies for this page
    // load; only the no-flash-on-next-boot optimisation is lost.
  }

  return preset;
}

/**
 * Apply the sidebar colour scheme. Same contract as applyThemePreset: unknown
 * values fall back to the default rather than rendering an unstyled nav.
 */
export function applyNavStyle(id) {
  const nav = isValidNavStyle(id) ? id : DEFAULT_NAV_STYLE;

  if (typeof document !== 'undefined') {
    if (nav === DEFAULT_NAV_STYLE) {
      // :root already holds the dark nav tokens; no attribute needed.
      document.documentElement.removeAttribute('data-nav');
    } else {
      document.documentElement.setAttribute('data-nav', nav);
    }
  }

  try {
    window.localStorage.setItem(NAV_STORAGE_KEY, nav);
  } catch (e) {
    // Storage blocked; theme still applies for this page load.
  }

  return nav;
}
